/**
 * Google Analytics Service for Vivideo
 * Centralized analytics tracking with user context
 *
 * Also dual-dispatches every event to Amplitude (via amplitudeService) with
 * Amplitude's own "Object Verb" taxonomy. GA and Amplitude are fully
 * independent: GA keeps its `snake_case` names for existing Looker / Ads
 * dashboards, Amplitude gets fresh business-oriented event names and its own
 * user-property model. Call sites stay unchanged.
 */

import {
  identifyAmplitudeUser,
  resetAmplitudeUser,
  trackAmpSignupStarted,
  trackAmpUserSignedUp,
  trackAmpUserLoggedIn,
  trackAmpUserLoggedOut,
  trackAmpPaywallViewed,
  trackAmpPaywallCtaClicked,
  trackAmpVideoPreviewReady,
  trackAmpPlanCardGenerateClicked,
  trackAmpCheckoutStarted,
  trackAmpPurchaseCompleted,
  trackAmpCheckoutAbandoned,
  trackAmpCancelReasonSelected,
  trackAmpExitOfferShown,
  trackAmpExitOfferClicked,
  trackAmpExitOfferDismissed,
  trackAmpExitOfferExpired,
  trackAmpEmailOfferViewed,
  trackAmpBillingPeriodToggled,
  trackAmpGenerationRequested,
  trackAmpFirstGenerationRequested,
  trackAmpVideoPlayed,
  trackAmpVideoDownloaded,
  trackAmpVideoShared,
  trackAmpVideoRemixStarted,
  trackAmpFullscreenBlocked,
  trackAmpFolderCreated,
  trackAmpFileRenamed,
  trackAmpFileMoved,
  trackAmpFileDeleted,
  trackAmpExamplesViewed,
  trackAmpUpgradeBannerClicked,
  trackAmpLanguageChanged,
  trackAmpAppShared,
  trackAmpBugReported,
  trackAmpHelpRequested,
  trackAmpErrorShown,
} from './amplitudeService';
import { clearMetaMatching, rememberMetaMatching, takeMetaCheckout } from './metaPixel';

// Declare gtag, fbq, and oaiq for TypeScript
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    oaiq?: (...args: unknown[]) => void;
  }
}

// Firebase GA4 Measurement ID - use this for all analytics events
// This prevents duplicate events when multiple gtag configs exist (e.g., Google Ads + Firebase)
const GA4_MEASUREMENT_ID = 'G-6BTP7XQEHX';

// User context interface
interface UserContext {
  vivideo_user_id?: string;
  user_email?: string;
  user_plan?: string;
  credits_remaining?: number;
  is_pro?: boolean;
  ab_group?: number;
  /** The user's current A/B arm — normalised into the Amplitude `video_provider` user property. */
  video_provider?: string | null;
}

// Store user context for all events
let userContext: UserContext = {};

/**
 * Set user context to be included in all events
 */
export const setAnalyticsUserContext = (context: UserContext) => {
  userContext = { ...context };
  
  // Also set user ID in GA4 if available
  if (typeof window.gtag === 'function' && context.vivideo_user_id) {
    window.gtag('set', { user_id: context.vivideo_user_id });
  }

  // Hashed em / external_id for the pixel's next init (index.html), matching the server CAPI.
  rememberMetaMatching(context.user_email, context.vivideo_user_id);

  if (context.vivideo_user_id) {
    identifyAmplitudeUser({
      vivideo_user_id: context.vivideo_user_id,
      user_email: context.user_email,
      user_plan: context.user_plan,
      credits_remaining: context.credits_remaining,
      is_pro: context.is_pro,
      ab_group: context.ab_group,
      video_provider: context.video_provider,
    });
  }
};

/**
 * Clear user context (on logout)
 */
export const clearAnalyticsUserContext = () => {
  userContext = {};
  resetAmplitudeUser();
  clearMetaMatching();
};

/**
 * Base tracking function with user context
 * Uses send_to parameter to prevent duplicate events when multiple gtag configs exist
 */
const trackEvent = (eventName: string, params: Record<string, unknown> = {}) => {
  if (typeof window.gtag !== 'function') {
    console.log(`[Analytics] gtag not available - ${eventName}:`, params);
    return;
  }

  // GA4 must not receive plain-text email (Google PII policy); Amplitude / Meta hashing keep it.
  const eventParams: Record<string, unknown> = {
    ...params,
    ...userContext,
    // CRITICAL: send_to prevents event from being broadcast to ALL configured tags
    // This fixes duplicate purchase events caused by Firebase + Google Ads tags
    send_to: GA4_MEASUREMENT_ID,
  };
  delete eventParams.user_email;

  window.gtag('event', eventName, eventParams);
  console.log(`[Analytics] ${eventName}:`, eventParams);
};

/**
 * Meta Pixel tracking helper. Mirrors GA events to Facebook so the Meta ads
 * campaign can optimize against the same funnel (Lead, InitiateCheckout,
 * Purchase). Safe no-op if the pixel snippet hasn't loaded yet.
 *
 * `eventID` (when provided) is the Meta-side dedup key against server-side
 * Conversions API events sharing the same ID — we use the Stripe transaction
 * id for purchases so a future CAPI hook won't double-count.
 */
const trackFbq = (
  eventName: string,
  params: Record<string, unknown> = {},
  eventID?: string,
  method: 'track' | 'trackCustom' = 'track',
) => {
  if (typeof window.fbq !== 'function') return;
  if (eventID) {
    window.fbq(method, eventName, params, { eventID });
  } else {
    window.fbq(method, eventName, params);
  }
};

/**
 * OpenAI ChatGPT Ads measurement pixel helper. Mirrors GA / Meta events so the
 * ChatGPT Ads campaign can optimize against the same funnel. Safe no-op if the
 * pixel snippet hasn't loaded yet.
 *
 * Per OpenAI's spec, monetary `amount` must be an integer in the lowest
 * denomination (e.g. cents for USD). Use `usdToCents` for conversion.
 */
const trackOaiq = (
  eventName: string,
  type: 'customer_action' | 'plan_enrollment' | 'contents' | 'custom',
  params: Record<string, unknown> = {},
) => {
  if (typeof window.oaiq !== 'function') return;
  window.oaiq('measure', eventName, { type, ...params });
};

const usdToCents = (value: number): number => Math.round(value * 100);

// ============================================
// AUTHENTICATION EVENTS
// ============================================

/**
 * Track an auth CTA press — BEFORE the provider round-trip. Together with
 * sign_up/login (which fire only after auth succeeds) this measures drop-off
 * inside the OAuth / magic-link step itself.
 */
export const trackSignupStarted = (method: 'google' | 'apple' | 'microsoft' | 'email' | 'bypass') => {
  trackEvent('signup_started', {
    method,
  });
  trackAmpSignupStarted(method);
};

/**
 * Track user sign up
 */
export const trackSignUp = (method: 'google' | 'apple' | 'microsoft' | 'email' | 'bypass') => {
  trackEvent('sign_up', {
    method,
  });
  trackAmpUserSignedUp(method);
  trackFbq('CompleteRegistration', {
    registration_method: method,
    status: 'completed',
  });
  trackOaiq('registration_completed', 'customer_action', {
    amount: 0,
    currency: 'USD',
  });
};

const SIGNUP_TRACKED_PREFIX = 'vivideo_signup_tracked_';
const signupsThisPage = new Set<string>();

/**
 * sign_up / CompleteRegistration once per new account, whichever auth path saw the backend's
 * 201 first (onAuthChange and the sign-in handler race on get-user-id). True when it fired.
 */
export const trackSignUpOnce = (
  userId: string,
  method: 'google' | 'apple' | 'microsoft' | 'email' | 'bypass',
): boolean => {
  if (wasSignUpTracked(userId)) return false;
  signupsThisPage.add(userId);
  try {
    localStorage.setItem(SIGNUP_TRACKED_PREFIX + userId, String(Date.now()));
  } catch {
    /* storage blocked: the in-page guard still holds */
  }
  trackSignUp(method);
  return true;
};

export const wasSignUpTracked = (userId: string): boolean => {
  try {
    return !!localStorage.getItem(SIGNUP_TRACKED_PREFIX + userId);
  } catch {
    return signupsThisPage.has(userId); // storage blocked: this page's memory is all there is
  }
};

/**
 * Track user login
 */
export const trackLogin = (method: 'google' | 'apple' | 'microsoft' | 'email' | 'bypass') => {
  trackEvent('login', {
    method,
  });
  // Owner taxonomy: 'Logged In' {ab_group} — userContext is populated by
  // setUser() before AuthContext fires this, so ab_group is available.
  trackAmpUserLoggedIn(method, userContext.ab_group);
};

/**
 * Track a failed sign-in attempt. `method` is the provider the user tried and
 * `errorCode` is the Firebase/GIS error code (e.g. auth/network-request-failed).
 * GA only — this is a funnel-diagnostics event with no Amplitude twin, mirroring
 * generation_failed. User-cancelled dismissals are intentionally NOT tracked as
 * errors by the caller.
 */
export const trackLoginError = (
  method: 'google' | 'apple' | 'microsoft' | 'email' | 'onetap' | 'bypass',
  errorCode: string,
) => {
  trackEvent('login_error', {
    method,
    error_code: errorCode,
  });
};

/**
 * Track user logout
 */
export const trackLogout = () => {
  trackEvent('logout', {});
  trackAmpUserLoggedOut();
};

// ============================================
// SUBSCRIPTION & MONETIZATION EVENTS
// ============================================

/**
 * Owner taxonomy: paywall = landing on /subscription. Fires there only.
 */
export const trackPaywallShown = (context: string, emailProps?: Record<string, string>) => {
  trackEvent('paywall_shown', {
    paywall_context: context,
  });
  // emailProps: recovery-email email_sequence/step/variant/campaign (Amplitude only).
  if (emailProps) trackAmpPaywallViewed(context, emailProps);
  else trackAmpPaywallViewed(context);
};

/**
 * A click that routes the user to the paywall (preview play/download/share/
 * fullscreen, upgrade-popup CTA). `action` says which control.
 */
export const trackPaywallCtaClicked = (action: string) => {
  trackEvent('paywall_cta_clicked', { action });
  trackAmpPaywallCtaClicked(action);
};

/**
 * The fake "your video is ready" result rendered — V2 blurred gate (with mode:
 * 'manual' | 'autogenerate' | 'agentic') or V1 preview modal (no mode).
 */
export const trackVideoPreviewReady = (mode?: string) => {
  trackEvent('video_preview_ready', mode ? { mode } : {});
  trackAmpVideoPreviewReady(mode);
};

/** V2 plan card's glowing Generate pressed (the SUBMIT is Generation Requested). */
export const trackPlanCardGenerateClicked = (mode: string) => {
  trackEvent('plan_card_generate_clicked', { mode });
  trackAmpPlanCardGenerateClicked(mode);
};

/**
 * Track when user begins checkout
 */
export const trackBeginCheckout = (planType: string, price: number) => {
  trackEvent('begin_checkout', {
    plan_type: planType,
    value: price,
    currency: 'USD',
  });
  trackAmpCheckoutStarted(planType, price);
  trackFbq('InitiateCheckout', {
    content_name: planType,
    content_category: planType.startsWith('credits') ? 'credits' : 'subscription',
    value: price,
    currency: 'USD',
  });
  trackOaiq('checkout_started', 'contents', {
    amount: usdToCents(price),
    currency: 'USD',
    contents: [
      {
        item_id: planType,
        item_name: planType,
        item_category: planType.startsWith('credits') ? 'credits' : 'subscription',
        quantity: 1,
        item_price: usdToCents(price),
      },
    ],
  });
};

/**
 * Track successful purchase (new event alongside existing conversion)
 * GA4 uses transaction_id for deduplication - same transaction_id won't be counted twice
 */
export const trackPurchase = (params: {
  planType: string;
  value: number;
  credits?: number;
  isSubscription: boolean;
  transactionId: string; // Required for GA4 deduplication
  /** Meta only: an existing customer's plan change, or a $0 card-verified trial. */
  metaEvent?: 'PlanUpgrade' | 'StartTrial';
}) => {
  trackEvent('purchase', {
    transaction_id: params.transactionId, // GA4 deduplication key
    plan_type: params.planType,
    value: params.value,
    currency: 'USD',
    credits_purchased: params.credits,
    is_subscription: params.isSubscription,
    transaction_type: params.isSubscription ? 'subscription' : 'credits',
  });
  // Client-side echo only — authoritative revenue is server-side
  // (stripe-webhook / revenuecat-webhook) so no $revenue attached here.
  trackAmpPurchaseCompleted(params);
  trackMetaSubscription(params);
  if (params.isSubscription) {
    trackOaiq('subscription_created', 'plan_enrollment', {
      plan_id: params.planType,
      amount: usdToCents(params.value),
      currency: 'USD',
    });
  }
};

/**
 * Meta gets Purchase only for a FIRST subscription, with what Stripe charges and the txn id as
 * eventID (the server CAPI twin uses the same id). The checkout record kept before the redirect
 * decides; without it nothing is sent here and the server event stands alone.
 */
const trackMetaSubscription = (params: {
  planType: string;
  value: number;
  transactionId: string;
  metaEvent?: 'PlanUpgrade' | 'StartTrial';
}) => {
  const id = params.transactionId;
  if (params.metaEvent === 'PlanUpgrade') {
    trackFbq('PlanUpgrade', { value: params.value, currency: 'USD', content_ids: [params.planType] }, id, 'trackCustom');
    return;
  }
  if (params.metaEvent === 'StartTrial') {
    trackFbq('StartTrial', { value: 0, currency: 'USD', content_ids: [params.planType], order_id: id }, id);
    return;
  }
  const checkout = takeMetaCheckout(id);
  if (!checkout) return;
  const value = typeof checkout.value === 'number' ? checkout.value : params.value;
  const currency = (checkout.currency || 'USD').toUpperCase();
  const data = {
    value,
    currency,
    content_type: 'product',
    content_ids: [params.planType],
    content_name: params.planType,
    order_id: id,
  };
  if (checkout.first === false) {
    trackFbq('Resubscribe', data, id, 'trackCustom');
  } else {
    // first === undefined: a backend without the first_subscription field; keep the old Purchase.
    trackFbq(value > 0 || checkout.first === undefined ? 'Purchase' : 'StartTrial', data, id);
  }
};

/**
 * Track checkout canceled
 */
export const trackCheckoutCanceled = (emailProps?: Record<string, string>) => {
  trackEvent('checkout_canceled', {});
  if (emailProps) trackAmpCheckoutAbandoned(emailProps);
  else trackAmpCheckoutAbandoned();
};

/* ===== Recovery / exit offer ($4.99 first week) ===== */

/** Offer overlay shown once, when a free user leaves the paywall without checkout. */
export const trackExitOfferShown = () => {
  trackEvent('exit_offer_shown', {});
  trackAmpExitOfferShown();
};

/** Offer CTA tapped, from either the overlay or the persistent banner. */
export const trackExitOfferClicked = (surface: 'overlay' | 'banner') => {
  trackEvent('exit_offer_clicked', { surface });
  trackAmpExitOfferClicked(surface);
};

/** Overlay dismissed (offer keeps running in the banner until it expires). */
export const trackExitOfferDismissed = () => {
  trackEvent('exit_offer_dismissed', {});
  trackAmpExitOfferDismissed();
};

/** The 10-minute deadline elapsed with no purchase. */
export const trackExitOfferExpired = () => {
  trackEvent('exit_offer_expired', {});
  trackAmpExitOfferExpired();
};

/** Recovery-email offer shown live on the paywall (offer id, price, ISO deadline). */
export const trackEmailOfferViewed = (offer: string, priceCents: number, expiresAt: string) => {
  trackEvent('email_offer_viewed', { offer, price_cents: priceCents, expires_at: expiresAt });
  trackAmpEmailOfferViewed(offer, priceCents, expiresAt);
};

/**
 * Track cancel reason selection in recovery flow
 */
export const trackCancelReasonSelected = (reason: 'browsing' | 'trust' | 'info') => {
  trackEvent('cancel_reason_selected', {
    cancel_reason: reason,
  });
  trackAmpCancelReasonSelected(reason);
};

/**
 * Track billing period toggle (weekly/yearly switch)
 */
export const trackBillingPeriodToggle = (newPeriod: 'weekly' | 'monthly' | 'yearly', variant: string) => {
  trackEvent('billing_period_toggle', {
    new_period: newPeriod,
    ab_variant: variant,
  });
  trackAmpBillingPeriodToggled(newPeriod, variant);
};

/**
 * Track credit purchase
 * transaction_id is used by GA4 for deduplication - same ID won't be counted twice
 */
export const trackCreditsPurchase = (creditAmount: number, price: number, transactionId: string) => {
  trackEvent('credits_purchase', {
    transaction_id: transactionId,
    credit_amount: creditAmount,
    value: price,
    currency: 'USD',
  });
  trackAmpPurchaseCompleted({
    planType: 'credits',
    value: price,
    credits: creditAmount,
    isSubscription: false,
    transactionId,
  });
  // Top-ups are not new customers: a custom event, so Purchase optimisation stays clean.
  trackFbq(
    'CreditTopUp',
    {
      value: price,
      currency: 'USD',
      content_ids: ['credits'],
      num_items: creditAmount,
    },
    transactionId,
    'trackCustom',
  );
};

// ============================================
// VIDEO GENERATION EVENTS
// ============================================

/**
 * Track when generation starts
 */
export const trackGenerationStarted = (params: {
  operationType: string;
  duration?: string;
  aspectRatio?: string;
  resolution?: string;
  hasImage?: boolean;
  imageCount?: number;
  /** Owner taxonomy — V1: 'manual' | 'auto'; V2: 'manual' | 'autogenerate' | 'agentic'. */
  mode?: string;
  /** Owner taxonomy: was the requester on the free tier at submit time. */
  freeUser?: boolean;
}) => {
  trackEvent('generation_started', {
    operation_type: params.operationType,
    duration: params.duration,
    aspect_ratio: params.aspectRatio,
    resolution: params.resolution,
    has_image: params.hasImage,
    image_count: params.imageCount,
    generation_mode: params.mode,
    free_user: params.freeUser,
  });
  trackAmpGenerationRequested(params);
};

/**
 * Track when generation completes successfully.
 *
 * GA only — Amplitude receives the authoritative `Generation Completed` event
 * from the server-side fal / heygen webhooks with provider, credits, and
 * duration, so we intentionally do NOT dual-dispatch here (would double-count
 * on Amplitude). Client may fire before the video actually finishes rendering.
 */
export const trackGenerationCompleted = (params: {
  operationType: string;
  orderId?: string;
}) => {
  trackEvent('generation_completed', {
    operation_type: params.operationType,
    order_id: params.orderId,
  });
};

/**
 * Track when generation fails.
 *
 * GA only — Amplitude receives `Generation Failed` from fal / heygen webhooks
 * with the real provider error message, so no dual-dispatch here.
 */
export const trackGenerationFailed = (params: {
  operationType: string;
  errorMessage?: string;
}) => {
  trackEvent('generation_failed', {
    operation_type: params.operationType,
    error_message: params.errorMessage,
  });
};

/**
 * Track first generation (activation metric)
 */
export const trackFirstGeneration = (operationType: string) => {
  // Check if user has generated before
  const hasGeneratedBefore = localStorage.getItem('vivideo_has_generated');
  if (!hasGeneratedBefore) {
    trackEvent('first_generation', {
      operation_type: operationType,
    });
    trackAmpFirstGenerationRequested(operationType);
    localStorage.setItem('vivideo_has_generated', 'true');
  }
};

// ============================================
// CONTENT ENGAGEMENT EVENTS
// ============================================

/**
 * Track video playback
 */
export const trackVideoPlayed = (operationType: string) => {
  trackEvent('video_played', {
    operation_type: operationType,
  });
  trackAmpVideoPlayed(operationType);
};

/**
 * Track video download
 *
 * `ampExtra` rides along to Amplitude only (order/provider/captions context the
 * V2 player knows). GA's `video_downloaded` payload stays exactly as it was.
 */
export const trackVideoDownloaded = (operationType: string, ampExtra?: Record<string, unknown>) => {
  trackEvent('video_downloaded', {
    operation_type: operationType,
  });
  trackAmpVideoDownloaded(operationType, ampExtra);
};

/**
 * Track video share
 *
 * `ampExtra` rides along to Amplitude only (share_method / order / provider the
 * call site knows, and `operation_type` when Amplitude needs a different one than
 * GA). GA's `video_shared` payload stays exactly as it was.
 */
export const trackVideoShared = (operationType: string, ampExtra?: Record<string, unknown>) => {
  trackEvent('video_shared', {
    operation_type: operationType,
  });
  trackAmpVideoShared(operationType, ampExtra);
};

/**
 * Track video remix started
 */
export const trackVideoRemixStarted = (originalOperationType: string) => {
  trackEvent('video_remix_started', {
    original_operation_type: originalOperationType,
  });
  trackAmpVideoRemixStarted(originalOperationType);
};

/**
 * Track fullscreen blocked for free users
 */
export const trackFullscreenBlocked = () => {
  trackEvent('fullscreen_blocked', {});
  trackAmpFullscreenBlocked();
};

// ============================================
// FILE MANAGEMENT EVENTS
// ============================================

/**
 * Track folder creation
 */
export const trackFolderCreated = () => {
  trackEvent('folder_created', {});
  trackAmpFolderCreated();
};

/**
 * Track file renamed
 */
export const trackFileRenamed = () => {
  trackEvent('file_renamed', {});
  trackAmpFileRenamed();
};

/**
 * Track file moved
 */
export const trackFileMoved = () => {
  trackEvent('file_moved', {});
  trackAmpFileMoved();
};

/**
 * Track file deleted
 */
export const trackFileDeleted = (operationType?: string) => {
  trackEvent('file_deleted', {
    operation_type: operationType,
  });
  trackAmpFileDeleted(operationType);
};

// ============================================
// NAVIGATION & DISCOVERY EVENTS
// ============================================

/**
 * Track page views.
 *
 * GA only — Amplitude `Page Viewed` is fired globally on every route change
 * by <AmplitudeRouteTracker/> in App.tsx, so dispatching here would
 * double-count on Amplitude.
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Track examples viewed
 */
export const trackExamplesViewed = () => {
  trackEvent('examples_viewed', {});
  trackAmpExamplesViewed();
};

/**
 * Track upgrade banner click
 */
export const trackUpgradeBannerClicked = (location: string) => {
  trackEvent('upgrade_banner_clicked', {
    banner_location: location,
  });
  trackAmpUpgradeBannerClicked(location);
};

// ============================================
// USER SETTINGS EVENTS
// ============================================

/**
 * Track language change
 */
export const trackLanguageChanged = (newLanguage: string) => {
  trackEvent('language_changed', {
    new_language: newLanguage,
  });
  trackAmpLanguageChanged(newLanguage);
};

// ============================================
// SUPPORT & ENGAGEMENT EVENTS
// ============================================

/**
 * Track app shared
 */
export const trackAppShared = () => {
  trackEvent('app_shared', {});
  trackAmpAppShared();
};

/**
 * Track bug reported
 */
export const trackBugReported = () => {
  trackEvent('bug_reported', {});
  trackAmpBugReported();
};

/**
 * Track help requested
 */
export const trackHelpRequested = () => {
  trackEvent('help_requested', {});
  trackAmpHelpRequested();
};

// ============================================
// ERROR EVENTS
// ============================================

/**
 * Track error shown to user
 */
export const trackErrorShown = (errorType: string, errorMessage?: string) => {
  trackEvent('error_shown', {
    error_type: errorType,
    error_message: errorMessage,
  });
  trackAmpErrorShown(errorType, errorMessage);
};

// Export analytics service as default
const analyticsService = {
  setUserContext: setAnalyticsUserContext,
  clearUserContext: clearAnalyticsUserContext,
  // Auth
  trackSignUp,
  trackSignUpOnce,
  trackLogin,
  trackLoginError,
  trackLogout,
  // Subscription
  trackPaywallShown,
  trackBeginCheckout,
  trackPurchase,
  trackCheckoutCanceled,
  trackCancelReasonSelected,
  trackCreditsPurchase,
  trackBillingPeriodToggle,
  // Generation
  trackGenerationStarted,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackFirstGeneration,
  // Engagement
  trackVideoPlayed,
  trackVideoDownloaded,
  trackVideoShared,
  trackVideoRemixStarted,
  trackFullscreenBlocked,
  // File management
  trackFolderCreated,
  trackFileRenamed,
  trackFileMoved,
  trackFileDeleted,
  // Navigation
  trackPageView,
  trackExamplesViewed,
  trackUpgradeBannerClicked,
  // Settings
  trackLanguageChanged,
  // Support
  trackAppShared,
  trackBugReported,
  trackHelpRequested,
  // Errors
  trackErrorShown,
};

export default analyticsService;
